// Dear Yesterday — small progressive enhancements for the dy-* sections.
// Everything here is optional: without JS, videos fall back to native
// controls and rails scroll natively.

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

function publish(name, data) {
  try {
    window.Shopify?.analytics?.publish?.(name, data);
  } catch (error) {
    // Analytics must never break the storefront.
  }
}

/**
 * <dy-video> — click-to-play video slot.
 * Shopify's video_tag renders with preload="none" and a poster, so nothing
 * downloads until the shopper taps play. Only one slot plays at a time.
 * data-mode="ambient": muted loop that plays only while on screen and
 * never when the shopper prefers reduced motion.
 */
class DyVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video) return;

    this.video.removeAttribute('controls');
    this.video.playsInline = true;

    if (this.dataset.mode === 'ambient') {
      this.#setupAmbient();
      return;
    }

    this.playButton = this.querySelector('.dy-video__play');
    this.playButton?.addEventListener('click', () => this.play());

    if (this.dataset.ui === 'overlay') {
      this.#setupOverlay();
      return;
    }

    this.muteButton = this.querySelector('.dy-video__mute');
    this.muteButton?.addEventListener('click', () => this.#toggleSound());
    if (this.dataset.muted === 'true') {
      // Tap the clip itself to pause / resume once it has started.
      this.video.addEventListener('click', () => {
        if (!this.classList.contains('is-playing')) return;
        if (this.video.paused) this.video.play().catch(() => {});
        else this.video.pause();
      });
    }
    this.video.addEventListener('ended', () => this.reset());
    this.video.addEventListener('pause', () => {
      if (this.dataset.muted === 'true') return;
      if (!this.video.ended && this.video.currentTime > 0) this.video.setAttribute('controls', '');
    });
  }

  /**
   * data-ui="overlay": big play button over the cover. Always muted; tapping
   * the clip pauses it and brings the play button back. Starting one clip
   * pauses every other one.
   */
  #setupOverlay() {
    const video = this.video;
    video.muted = true;
    video.addEventListener('volumechange', () => {
      if (!video.muted) video.muted = true;
    });
    video.addEventListener('click', () => {
      if (!video.paused) video.pause();
    });
    video.addEventListener('pause', () => this.classList.remove('is-playing'));
    video.addEventListener('play', () => this.classList.add('is-playing'));
    video.addEventListener('ended', () => {
      video.currentTime = 0;
      this.classList.remove('is-playing');
    });
  }

  play() {
    document.querySelectorAll('dy-video').forEach((other) => {
      if (other !== this) other.pause();
    });
    if (this.dataset.ui === 'overlay') {
      // Silent clips: always muted, a tap plays or pauses, no sound controls.
      const video = this.video;
      if (video.preload === 'none') video.preload = 'auto';
      video.muted = true;
      video.play().catch(() => {});
      publish('dy_video_play', { slot: this.dataset.slot || '', page: window.location.pathname });
      return;
    }
    this.classList.add('is-playing');
    if (this.dataset.muted === 'true') {
      // Short clips in a row: play muted, keep the frame clean, offer a sound toggle.
      this.video.muted = true;
      if (this.video.preload === 'none') this.video.preload = 'auto';
      if (this.muteButton) this.muteButton.hidden = false;
      this.video.play().catch(() => {});
      publish('dy_video_play', { slot: this.dataset.slot || '', page: window.location.pathname });
      return;
    }
    this.video.muted = false;
    this.video.setAttribute('controls', '');
    this.video.play().catch(() => {
      // Autoplay with sound refused: retry muted so the tap still does something.
      this.video.muted = true;
      this.video.play().catch(() => {});
    });
    publish('dy_video_play', { slot: this.dataset.slot || '', page: window.location.pathname });
  }

  pause() {
    this.video?.pause();
  }

  #toggleSound() {
    this.video.muted = !this.video.muted;
    const on = !this.video.muted;
    this.muteButton.setAttribute('aria-pressed', String(on));
    this.muteButton.setAttribute('aria-label', on ? this.muteButton.dataset.labelMute : this.muteButton.dataset.labelUnmute);
  }

  reset() {
    this.classList.remove('is-playing');
    if (this.muteButton) this.muteButton.hidden = true;
    this.video.removeAttribute('controls');
    this.video.currentTime = 0;
  }

  #setupAmbient() {
    const video = this.video;
    video.muted = true;
    video.loop = true;

    if (reduceMotion.matches) {
      // Leave the poster frame showing; offer a play control instead.
      this.dataset.mode = 'click';
      this.connectedCallback();
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (video.preload === 'none') video.preload = 'auto';
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.35 }
    );
    observer.observe(this);

    const mute = this.querySelector('.dy-video__mute');
    mute?.addEventListener('click', () => {
      video.muted = !video.muted;
      mute.setAttribute('aria-pressed', String(!video.muted));
      mute.setAttribute('aria-label', video.muted ? mute.dataset.labelUnmute : mute.dataset.labelMute);
    });
  }
}

/**
 * <dy-rail> — previous / next buttons for a horizontally scrolling list.
 */
class DyRail extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.dy-rail');
    this.prev = this.querySelector('[data-rail-prev]');
    this.next = this.querySelector('[data-rail-next]');
    if (!this.track || !this.prev || !this.next) return;

    const step = () => {
      const item = this.track.firstElementChild;
      return item ? item.getBoundingClientRect().width + 20 : this.track.clientWidth * 0.8;
    };

    this.prev.addEventListener('click', () => this.track.scrollBy({ left: -step(), behavior: this.#behavior() }));
    this.next.addEventListener('click', () => this.track.scrollBy({ left: step(), behavior: this.#behavior() }));
    this.track.addEventListener('scroll', () => this.#update(), { passive: true });
    new ResizeObserver(() => this.#update()).observe(this.track);
    this.#update();
  }

  #behavior() {
    return reduceMotion.matches ? 'auto' : 'smooth';
  }

  #update() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const scrollable = scrollWidth - clientWidth > 4;
    this.querySelector('.dy-rail__navs')?.toggleAttribute('hidden', !scrollable);
    this.prev.disabled = scrollLeft <= 4;
    this.next.disabled = scrollLeft >= scrollWidth - clientWidth - 4;
  }
}

/**
 * Lightweight conversion events (Settings → Customer events can subscribe):
 *   dy_cta_click   — any element with data-dy-cta="<location>"
 *   dy_video_play  — a shopper starts a video slot
 * Shopify already tracks product views, add to cart, checkout and purchase.
 */
document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target.closest('[data-dy-cta]') : null;
  if (!target) return;
  publish('dy_cta_click', {
    location: target.getAttribute('data-dy-cta'),
    label: target.textContent.trim().replace(/\s+/g, ' ').slice(0, 80),
    href: target.getAttribute('href') || null,
    page: window.location.pathname,
  });
});

if (!customElements.get('dy-video')) customElements.define('dy-video', DyVideo);
if (!customElements.get('dy-rail')) customElements.define('dy-rail', DyRail);

/**
 * <dy-loop-video>: silent clip that loops while on screen and pauses when
 * scrolled away. If the phone blocks autoplay (e.g. iPhone Low Power Mode),
 * it starts on the visitor's first touch or scroll.
 */
class DyLoopVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.video.muted = true;
    this.observer = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
      if (this.visible) this.play();
      else this.video.pause();
    }, { rootMargin: '150px 0px', threshold: 0.2 });
    this.observer.observe(this);
  }
  disconnectedCallback() { this.observer?.disconnect(); }
  play() {
    if (this.video.preload === 'none') this.video.preload = 'auto';
    this.video.play().catch(() => DyLoopVideo.waitForGesture());
  }
  static waitForGesture() {
    if (DyLoopVideo.waiting) return;
    DyLoopVideo.waiting = true;
    const events = ['touchstart', 'pointerdown', 'scroll', 'keydown'];
    const kick = () => {
      events.forEach((e) => removeEventListener(e, kick, true));
      DyLoopVideo.waiting = false;
      document.querySelectorAll('dy-loop-video').forEach((el) => { if (el.visible) el.video.play().catch(() => {}); });
    };
    events.forEach((e) => addEventListener(e, kick, { capture: true, passive: true }));
  }
}
if (!customElements.get('dy-loop-video')) customElements.define('dy-loop-video', DyLoopVideo);

/**
 * Bundle guard: Duo/Family lines are priced per bag and only sold in sets of
 * 2 / 4. If a cart ever holds an odd amount (e.g. via a direct link), round
 * it down to a full set (or remove it) before checkout.
 */
const DY_BUNDLE_SETS = { 'Duo Bundle': 2, 'Family Bundle': 4 };
async function dyFixBundles() {
  try {
    const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
    const updates = {};
    cart.items.forEach((item) => {
      const set = DY_BUNDLE_SETS[item.variant_title];
      if (set && item.quantity % set) updates[item.key] = item.quantity - (item.quantity % set);
    });
    if (!Object.keys(updates).length) return false;
    await fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates }) });
    return true;
  } catch (e) { return false; }
}
dyFixBundles().then((changed) => { if (changed && /\/cart\/?$/.test(location.pathname)) location.reload(); });
document.addEventListener('click', async (e) => {
  const btn = e.target.closest('button[name="checkout"], a[href="/checkout"], [href^="/checkout"]');
  if (!btn || btn.dataset.dyChecked) return;
  e.preventDefault(); e.stopPropagation();
  await dyFixBundles();
  btn.dataset.dyChecked = '1';
  btn.click();
}, true);
